import React from 'react';
import { profileService, CountryCode, Timezone } from '../../../services/profile/profileService';
import { getGenderTypes } from '../../../services/doctor-onboarding/doctorOnboardingService';
import type { GenderType } from '../../../types/doctorOnboarding';
import { apiClient } from '../../../services/api/apiClient';
import DateOfBirthPicker from '../../../components/DateOfBirthPicker';

interface BasicDetailsStepProps {
  data?: any;
  onChange: (data: any) => void;
  onNext: (data: any) => void;
  onBack: () => void;
  onValidChange?: (isValid: boolean) => void;
}

export interface BasicDetailsStepRef {
  validateAndProceed: () => void;
}

type FieldErrors = Partial<Record<string, string>>;
type ExtendedCountryCode = CountryCode & { min_digits?: number; max_digits?: number };

const BasicDetailsStep = React.forwardRef<BasicDetailsStepRef, BasicDetailsStepProps>(
  ({ data, onChange, onNext, onBack, onValidChange }, ref) => {
  const [countryCodes, setCountryCodes] = React.useState<
    ExtendedCountryCode[]
  >([]);
  const [timezones, setTimezones] = React.useState<
    Timezone[]
  >([]);
  const [genderTypes, setGenderTypes] = React.useState<GenderType[]>([]);
  const [phoneMinDigits, setPhoneMinDigits] = React.useState<number>(6);
  const [phoneMaxDigits, setPhoneMaxDigits] = React.useState<number>(15);
  const [formData, setFormData] = React.useState({
    first_name: data?.first_name || '',
    middle_name: data?.middle_name || '',
    last_name: data?.last_name || '',
    date_of_birth: data?.date_of_birth || '',
    gender: data?.gender || '',
    height_cm: data?.height_cm || '',
    weight_kg: data?.weight_kg || '',
    body_type: data?.body_type || '',
    activity_level: data?.activity_level || '',
    email: '', // No default email - must be entered by user
    phone_number: data?.phone_number || '',
    timezone: data?.timezone || 'Asia/Kolkata',
    timezone_id: data?.timezone_id || '',
    country_code_id: data?.country_code_id || '',
  });
  const [errors, setErrors] = React.useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = React.useState<FieldErrors>({});
  const [submitted, setSubmitted] = React.useState<boolean>(false);

  const validate = (dataToValidate = formData) => {
    const errs: string[] = [];
    const fieldErrs: FieldErrors = {};

    if (!dataToValidate.first_name.trim()) {
      errs.push('First name is required');
      fieldErrs.first_name = 'First name is required';
    }
    if (!dataToValidate.last_name.trim()) {
      errs.push('Last name is required');
      fieldErrs.last_name = 'Last name is required';
    }

    if (!dataToValidate.email.trim()) {
      errs.push('Email is required');
      fieldErrs.email = 'Email is required';
    }
    const emailRegex = /\S+@\S+\.\S+/;
    if (dataToValidate.email && !emailRegex.test(dataToValidate.email)) {
      errs.push('Email is invalid');
      fieldErrs.email = 'Email is invalid';
    }
    
    // Check if email belongs to a doctor (async validation will be done on submit)
    // This is a client-side check - backend will also validate

    if (!dataToValidate.phone_number.trim()) {
      errs.push('Phone number is required');
      fieldErrs.phone_number = 'Phone number is required';
    }
    if (dataToValidate.phone_number) {
      const len = dataToValidate.phone_number.replace(/\D/g, '').length;
      if (len < phoneMinDigits || len > phoneMaxDigits) {
        errs.push(`Phone number must be between ${phoneMinDigits} and ${phoneMaxDigits} digits`);
        fieldErrs.phone_number = `Phone number must be between ${phoneMinDigits} and ${phoneMaxDigits} digits`;
      }
    }

    if (!dataToValidate.date_of_birth) {
      errs.push('Date of birth is required');
      fieldErrs.date_of_birth = 'Date of birth is required';
    } else {
      const dob = new Date(dataToValidate.date_of_birth);
      const today = new Date();
      if (dob > today) {
        errs.push('Date of birth cannot be in the future');
        fieldErrs.date_of_birth = 'Date of birth cannot be in the future';
      } else {
        // Calculate age
        let age = today.getFullYear() - dob.getFullYear();
        const monthDiff = today.getMonth() - dob.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
          age--;
        }
        // Minimum age requirement: 18 years
        if (age < 18) {
          errs.push('You must be at least 18 years old to register');
          fieldErrs.date_of_birth = 'You must be at least 18 years old to register';
        }
      }
    }

    // Gender validation: mandatory
    if (!dataToValidate.gender || !dataToValidate.gender.trim()) {
      errs.push('Gender is required');
      fieldErrs.gender = 'Gender is required';
    }

    // Height validation: mandatory, integer between 30 and 300
    if (!dataToValidate.height_cm || !dataToValidate.height_cm.toString().trim()) {
      errs.push('Height is required');
      fieldErrs.height_cm = 'Please enter your height.';
    } else {
      const h = parseInt(dataToValidate.height_cm.toString().trim());
      if (isNaN(h) || h < 30 || h > 300) {
        errs.push('Height must be between 30 and 300 cm');
        fieldErrs.height_cm = 'Please enter a valid height (30-300 cm).';
      }
    }

    // Weight validation: mandatory, decimal between 10.0 and 400.0
    if (!dataToValidate.weight_kg || !dataToValidate.weight_kg.toString().trim()) {
      errs.push('Weight is required');
      fieldErrs.weight_kg = 'Please enter your weight.';
    } else {
      const w = parseFloat(dataToValidate.weight_kg.toString().trim());
      if (isNaN(w) || w < 10.0 || w > 400.0) {
        errs.push('Weight must be between 10 and 400 kg');
        fieldErrs.weight_kg = 'Please enter a valid weight (10-400 kg).';
      }
    }

    // Body type validation: mandatory, must be one of the allowed values
    if (!dataToValidate.body_type || !dataToValidate.body_type.trim()) {
      errs.push('Body type is required');
      fieldErrs.body_type = 'Please select your body type.';
    } else {
      const validBodyTypes = ['ectomorph', 'mesomorph', 'endomorph'];
      if (!validBodyTypes.includes(dataToValidate.body_type)) {
        errs.push('Please select a valid body type');
        fieldErrs.body_type = 'Please select a valid body type.';
      }
    }

    // Activity level validation: mandatory, must be one of the allowed values
    if (!dataToValidate.activity_level || !dataToValidate.activity_level.trim()) {
      errs.push('Activity level is required');
      fieldErrs.activity_level = 'Please select your activity level.';
    } else {
      const validActivityLevels = ['sedentary', 'lightly_active', 'moderately_active', 'very_active', 'super_active'];
      if (!validActivityLevels.includes(dataToValidate.activity_level)) {
        errs.push('Please select a valid activity level');
        fieldErrs.activity_level = 'Please select a valid activity level.';
      }
    }

    if (!dataToValidate.timezone) {
      errs.push('Timezone is required');
      fieldErrs.timezone = 'Timezone is required';
    }

    return { errs, fieldErrs };
  };

  const handleChange = (field: string, value: any) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);
    onChange(updated);
    // Do not validate on change; if already submitted, clear field error for this field
    if (submitted && fieldErrors[field]) {
      const newFieldErrors = { ...fieldErrors };
      delete newFieldErrors[field];
      setFieldErrors(newFieldErrors);
      // leave summary errors until next validation
    }
  };

  const validateAndProceed = async () => {
    setSubmitted(true);
    const { errs, fieldErrs } = validate();
    setErrors(errs);
    setFieldErrors(fieldErrs);
    onValidChange?.(errs.length === 0);
    if (errs.length > 0) return;

    // Check if email belongs to a doctor
    if (formData.email && formData.email.trim()) {
      try {
        // Check if email exists as a doctor's email
        const response = await apiClient.post('/api/v1/auth/email/start', {
          email: formData.email.trim()
        });
        
        // If the email exists and is a doctor's email, show error
        if (response.data && response.data.realm === 'doctor') {
          const newErrs = [...errs, 'This email belongs to a doctor account. Please use a different email address.'];
          const newFieldErrs = { ...fieldErrs, email: 'This email belongs to a doctor account. Please use a different email address.' };
          setErrors(newErrs);
          setFieldErrors(newFieldErrs);
          onValidChange?.(false);
          return;
        }
      } catch (error: any) {
        // If the endpoint doesn't exist or returns an error, we'll proceed
        // The backend will validate this during registration/onboarding
        console.warn('Could not verify email against doctor accounts:', error);
      }
    }

    const submitData = {
      ...formData,
      height_cm: formData.height_cm ? parseInt(formData.height_cm.toString().trim()) : undefined,
      weight_kg: formData.weight_kg ? parseFloat(formData.weight_kg.toString().trim()) : undefined,
    };
    onNext(submitData);
  };

  React.useImperativeHandle(ref, () => ({
    validateAndProceed,
  }));

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [codes, tzs, genders] = await Promise.all([
          profileService.getCountryCodes(),
          profileService.getTimezones(),
          getGenderTypes(),
        ]);
        if (cancelled) return;
        const extCodes = codes as ExtendedCountryCode[];
        setCountryCodes(extCodes);
        setTimezones(tzs);
        setGenderTypes(Array.isArray(genders) ? genders : []);

        const india = extCodes.find(
          (c: ExtendedCountryCode) =>
            c.country_name?.toLowerCase() === 'india' ||
            c.dial_code === '+91' ||
            c.dial_code === '91'
        );
        const indiaTz =
          tzs.find((t) => t.identifier === 'Asia/Kolkata') ||
          tzs.find((t) => t.identifier === 'Asia/Calcutta') ||
          tzs.find((t) => t.display_name?.toLowerCase().includes('india') && t.utc_offset === '+05:30') ||
          tzs.find((t) => t.utc_offset === '+05:30');

        setFormData((prev) => {
          let next = { ...prev };
          let changed = false;
          if (!prev.country_code_id && india) {
            next.country_code_id = india.id.toString();
            changed = true;
            if ((india as ExtendedCountryCode).min_digits != null) setPhoneMinDigits((india as ExtendedCountryCode).min_digits as number);
            if ((india as ExtendedCountryCode).max_digits != null) setPhoneMaxDigits((india as ExtendedCountryCode).max_digits as number);
          }
          if ((!prev.timezone_id || !prev.timezone) && indiaTz) {
            next.timezone = 'Asia/Kolkata';
            next.timezone_id = indiaTz.id.toString();
            changed = true;
          } else if (prev.timezone_id && (!prev.timezone || prev.timezone === '') && tzs.length) {
            const match = tzs.find((t) => t.id.toString() === prev.timezone_id);
            if (match) {
              next.timezone = match.identifier;
              changed = true;
            }
          }
          if (changed) {
            onChange(next);
          }
          return next;
        });
      } catch (err) {
        // swallow; keep manual entry
      }
    };
    load();
    return () => { cancelled = true; };
  }, [onChange]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    validateAndProceed();
  };

  return (
    <form onSubmit={handleSubmit}>
      {submitted && errors.length > 0 && (
        <div className="error-message" style={{ marginBottom: '16px' }}>
          <ul style={{ margin: 0, paddingLeft: '18px' }}>
            {errors.map((err) => (
              <li key={err}>{err}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">
            First Name <span className="required">*</span>
          </label>
          <input
            type="text"
            className="form-input"
            value={formData.first_name}
            onChange={(e) => handleChange('first_name', e.target.value)}
            required
          />
        {submitted && fieldErrors.first_name && <div className="field-error">{fieldErrors.first_name}</div>}
        </div>

        <div className="form-group">
          <label className="form-label">Middle Name</label>
          <input
            type="text"
            className="form-input"
            value={formData.middle_name}
            onChange={(e) => handleChange('middle_name', e.target.value)}
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">
          Last Name <span className="required">*</span>
        </label>
        <input
          type="text"
          className="form-input"
          value={formData.last_name}
          onChange={(e) => handleChange('last_name', e.target.value)}
          required
        />
        {submitted && fieldErrors.last_name && <div className="field-error">{fieldErrors.last_name}</div>}
      </div>

      <div className="form-grid">
        <div className="form-group">
          <DateOfBirthPicker
            label="Date of Birth (Must be 18+ years)"
            value={formData.date_of_birth}
            onChange={(date) => handleChange('date_of_birth', date || '')}
            required
            error={submitted && !!fieldErrors.date_of_birth}
            maxDate={(() => {
              // Set max date to 18 years ago (minimum age requirement)
              // Users must be at least 18 years old, so DOB must be at least 18 years in the past
              const today = new Date();
              const maxDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
              return maxDate;
            })()}
          />
          {submitted && fieldErrors.date_of_birth && <div className="field-error">{fieldErrors.date_of_birth}</div>}
        </div>

        <div className="form-group">
          <label className="form-label">
            Gender <span className="required">*</span>
          </label>
          <select
            className="form-select"
            value={formData.gender}
            onChange={(e) => handleChange('gender', e.target.value)}
            required
          >
            <option value="">Select...</option>
            {genderTypes.filter((g) => g.is_active).map((g) => (
              <option key={g.id} value={g.code}>
                {g.name}
              </option>
            ))}
          </select>
          {submitted && fieldErrors.gender && <div className="field-error">{fieldErrors.gender}</div>}
        </div>
      </div>

      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">
            Height (cm) <span className="required">*</span>
          </label>
          <input
            type="number"
            className="form-input"
            value={formData.height_cm}
            onChange={(e) => handleChange('height_cm', e.target.value)}
            min="30"
            max="300"
            required
            step="1"
          />
        {submitted && fieldErrors.height_cm && <div className="field-error">{fieldErrors.height_cm}</div>}
        </div>

        <div className="form-group">
          <label className="form-label">
            Weight (kg) <span className="required">*</span>
          </label>
          <input
            type="number"
            className="form-input"
            value={formData.weight_kg}
            onChange={(e) => handleChange('weight_kg', e.target.value)}
            min="10"
            max="400"
            required
            step="0.1"
          />
        {submitted && fieldErrors.weight_kg && <div className="field-error">{fieldErrors.weight_kg}</div>}
        </div>
      </div>

      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">
            Body Type <span className="required">*</span>
          </label>
          <select
            className="form-select"
            value={formData.body_type}
            onChange={(e) => handleChange('body_type', e.target.value)}
            required
          >
            <option value="">Select...</option>
            <option value="ectomorph">Lean / Slender build</option>
            <option value="mesomorph">Athletic / Balanced build</option>
            <option value="endomorph">Curvy / Sturdy build</option>
          </select>
        {submitted && fieldErrors.body_type && <div className="field-error">{fieldErrors.body_type}</div>}
        </div>

        <div className="form-group">
          <label className="form-label">
            Activity Level <span className="required">*</span>
          </label>
          <select
            className="form-select"
            value={formData.activity_level}
            onChange={(e) => handleChange('activity_level', e.target.value)}
            required
          >
            <option value="">Select...</option>
            <option value="sedentary">Sedentary</option>
            <option value="lightly_active">Lightly Active</option>
            <option value="moderately_active">Moderately Active</option>
            <option value="very_active">Very Active</option>
            <option value="super_active">Super Active</option>
          </select>
        {submitted && fieldErrors.activity_level && <div className="field-error">{fieldErrors.activity_level}</div>}
        </div>
      </div>

      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">
            Email <span className="required">*</span>
          </label>
          <input
            type="email"
            className="form-input"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            required
          />
        {submitted && fieldErrors.email && <div className="field-error">{fieldErrors.email}</div>}
        </div>

        <div className="form-group">
          <label className="form-label">
            Phone Number <span className="required">*</span>
          </label>
          <div className="phone-row">
            {countryCodes.length > 0 ? (
              <select
                className="form-select country-code-select"
                value={formData.country_code_id}
                onChange={(e) => {
                  const value = e.target.value;
                  handleChange('country_code_id', value);
                  const selected = countryCodes.find((c) => c.id.toString() === value);
                  if (selected) {
                    if (selected.min_digits) setPhoneMinDigits(selected.min_digits);
                    if (selected.max_digits) setPhoneMaxDigits(selected.max_digits);
                  }
                }}
              >
                <option value="">Code</option>
                {countryCodes.map((code) => (
                  <option key={code.id} value={code.id}>
                    {(code.dial_code?.startsWith('+') ? code.dial_code : `+${code.dial_code}`) || ''} ({code.country_name})
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                className="form-input country-code-input"
                value={formData.country_code_id}
                onChange={(e) => handleChange('country_code_id', e.target.value)}
                placeholder="Code"
              />
            )}
            <input
              type="tel"
              className="form-input phone-input"
              value={formData.phone_number}
              onChange={(e) => handleChange('phone_number', e.target.value)}
              required
              minLength={phoneMinDigits}
              maxLength={phoneMaxDigits}
              placeholder={`Min ${phoneMinDigits}, Max ${phoneMaxDigits} digits`}
            />
          </div>
          {submitted && fieldErrors.phone_number && <div className="field-error">{fieldErrors.phone_number}</div>}
        </div>
      </div>

      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">
            Timezone <span className="required">*</span>
          </label>
          {timezones.length > 0 ? (
            <select
              className="form-select"
              value={formData.timezone_id}
              onChange={(e) => {
                const selected = timezones.find((t) => t.id.toString() === e.target.value);
                const updated = {
                  ...formData,
                  timezone_id: e.target.value,
                  timezone: selected?.identifier || formData.timezone,
                };
                setFormData(updated);
                onChange(updated);
              }}
            >
              <option value="">Select timezone</option>
              {timezones.map((tz) => (
                <option key={tz.id} value={tz.id}>
                  {tz.display_name} ({tz.utc_offset})
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              className="form-input"
              value={formData.timezone}
              onChange={(e) => handleChange('timezone', e.target.value)}
              placeholder="e.g., America/Los_Angeles"
            />
          )}
          {submitted && fieldErrors.timezone && <div className="field-error">{fieldErrors.timezone}</div>}
        </div>
      </div>

      <button type="submit" style={{ display: 'none' }} />
    </form>
  );
});

BasicDetailsStep.displayName = 'BasicDetailsStep';

export default BasicDetailsStep;
